import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  taxTypeLabels,
  useJournalEntries,
  useVoidJournalEntry,
  type JournalEntryFilters,
} from '../hooks/useJournalEntries';
import { useAccounts } from '../hooks/useAccounts';
import { useCounterparties } from '../hooks/useCounterparties';
import type { JournalEntry } from '../types';

export default function JournalEntriesPage() {
  const [filters, setFilters] = useState<JournalEntryFilters>({});
  const [draft, setDraft] = useState<JournalEntryFilters>({});
  const { data: entries, isLoading, error } = useJournalEntries(filters);
  const { data: accounts } = useAccounts(true);
  const { data: counterparties } = useCounterparties(true);
  const voidMut = useVoidJournalEntry();

  const accountMap = useMemo(() => {
    const m = new Map<string, string>();
    accounts?.forEach((a) => m.set(a.id, `${a.code} ${a.name}`));
    return m;
  }, [accounts]);

  const counterpartyMap = useMemo(() => {
    const m = new Map<string, string>();
    counterparties?.forEach((cp) => m.set(cp.id, cp.name));
    return m;
  }, [counterparties]);

  const onVoid = async (entry: JournalEntry) => {
    const reason = prompt(`仕訳「${entry.description}」を取消します。\n取消理由を入力してください：`);
    if (!reason || reason.trim().length === 0) return;
    try {
      await voidMut.mutateAsync({ id: entry.id, voidReason: reason.trim() });
    } catch (err) {
      alert(err instanceof Error ? err.message : '取消失敗');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2>仕訳一覧</h2>
        <Link to="/journal-entries/new">
          <button className="primary">+ 新規仕訳</button>
        </Link>
      </div>

      {error && <div className="error-banner">{(error as Error).message}</div>}

      <div className="card">
        <div className="toolbar">
          <label>
            日付From{' '}
            <input
              type="date"
              value={draft.dateFrom ?? ''}
              onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value || undefined })}
            />
          </label>
          <label>
            日付To{' '}
            <input
              type="date"
              value={draft.dateTo ?? ''}
              onChange={(e) => setDraft({ ...draft, dateTo: e.target.value || undefined })}
            />
          </label>
          <label>
            摘要{' '}
            <input
              value={draft.description ?? ''}
              onChange={(e) => setDraft({ ...draft, description: e.target.value || undefined })}
            />
          </label>
          <label>
            ステータス{' '}
            <select
              value={draft.status ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, status: (e.target.value || undefined) as JournalEntryFilters['status'] })
              }
            >
              <option value="">全て</option>
              <option value="active">有効</option>
              <option value="voided">取消済</option>
            </select>
          </label>
          <button className="primary" onClick={() => setFilters(draft)}>
            検索
          </button>
          <button
            onClick={() => {
              setDraft({});
              setFilters({});
            }}
          >
            リセット
          </button>
        </div>
      </div>

      {isLoading && <div>読み込み中...</div>}

      {entries && (
        <table>
          <thead>
            <tr>
              <th style={{ width: 110 }}>取引日付</th>
              <th>摘要</th>
              <th>取引先</th>
              <th style={{ width: 100 }}>消費税</th>
              <th>仕訳明細</th>
              <th className="numeric" style={{ width: 130 }}>合計</th>
              <th style={{ width: 90 }}>状態</th>
              <th style={{ width: 110 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => {
              const total = (e.lines ?? [])
                .filter((l) => l.lineType === 'debit')
                .reduce((s, l) => s + l.amount, 0);
              return (
                <tr key={e.id}>
                  <td>{e.entryDate}</td>
                  <td>
                    {e.description}
                    {e.originalEntryId && (
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        ↩ 取消仕訳
                      </div>
                    )}
                  </td>
                  <td>{e.counterpartyId ? counterpartyMap.get(e.counterpartyId) ?? '—' : '—'}</td>
                  <td>{taxTypeLabels[e.taxType]}</td>
                  <td>
                    {(e.lines ?? []).map((l) => (
                      <div key={l.id} style={{ fontSize: 12 }}>
                        <span className="text-muted">{l.lineType === 'debit' ? '借' : '貸'}</span>{' '}
                        {accountMap.get(l.accountId) ?? l.accountId}{' '}
                        <span className="numeric">{l.amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </td>
                  <td className="numeric">{total.toLocaleString()}</td>
                  <td>
                    {e.status === 'active' ? (
                      <span className="badge active">有効</span>
                    ) : (
                      <span className="badge voided" title={e.voidReason ?? ''}>
                        取消済
                      </span>
                    )}
                  </td>
                  <td>
                    {e.status === 'active' && !e.originalEntryId && (
                      <button onClick={() => onVoid(e)} className="danger">
                        取消
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {entries.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: 24 }} className="text-muted">
                  該当する仕訳はありません
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
